import React from "react";
import { NEW_TEMPLATE_NAME } from "@components/Tax Pro Only/Modals/EmailModal/constants.ts";
import type { Template } from "@components/Tax Pro Only/Modals/EmailModal/types.ts";

/**
 * Props for the TemplateDropdownContent component
 */
export interface TemplateDropdownContentProps {
  selectedTemplateName: string;
  defaultTemplates: Template[];
  customTemplates: Template[];
  defaultTemplatesCollapsed: boolean;
  customTemplatesCollapsed: boolean;
  toggleDefaultTemplates: (e: React.MouseEvent | React.KeyboardEvent) => void;
  toggleCustomTemplates: (e: React.MouseEvent | React.KeyboardEvent) => void;
  onSelectTemplate: (templateName: string) => void;
  handleKeyDown: (
    e: React.KeyboardEvent,
    callback: (e: React.KeyboardEvent) => void
  ) => void;
}

/**
 * TemplateDropdownContent component displays the dropdown content for template selection
 */
const TemplateDropdownContent: React.FC<TemplateDropdownContentProps> = ({
  selectedTemplateName,
  defaultTemplates,
  customTemplates,
  defaultTemplatesCollapsed,
  customTemplatesCollapsed,
  toggleDefaultTemplates,
  toggleCustomTemplates,
  onSelectTemplate,
  handleKeyDown,
}) => {
  return (
    <div className="dropdown-content">
      {/* Actions section */}
      <div className="dropdown-item section-header">Actions</div>
      <button
        className={`dropdown-item action-item ${selectedTemplateName === NEW_TEMPLATE_NAME ? "selected" : ""}`}
        onClick={() => onSelectTemplate(NEW_TEMPLATE_NAME)}
        onKeyDown={(e) =>
          handleKeyDown(e, () => onSelectTemplate(NEW_TEMPLATE_NAME))
        }
        aria-pressed={selectedTemplateName === NEW_TEMPLATE_NAME}
      >
        + Add a template
      </button>

      {/* Default templates section */}
      {Array.isArray(defaultTemplates) && defaultTemplates.length > 0 && (
        <>
          <button
            className="dropdown-item section-header clickable"
            onClick={toggleDefaultTemplates}
            onKeyDown={(e) => handleKeyDown(e, toggleDefaultTemplates)}
            aria-expanded={!defaultTemplatesCollapsed}
          >
            <span>Default Templates</span>{" "}
            <span className="section-toggle" aria-hidden="true">
              {defaultTemplatesCollapsed ? "▼" : "▲"}
            </span>
          </button>
          {!defaultTemplatesCollapsed &&
            defaultTemplates.map((template) => (
              <button
                key={template.id}
                className={`dropdown-item ${selectedTemplateName === template.name ? "selected" : ""}`}
                onClick={() => onSelectTemplate(template.name)}
                onKeyDown={(e) =>
                  handleKeyDown(e, () => onSelectTemplate(template.name))
                }
                aria-pressed={selectedTemplateName === template.name}
              >
                {template.name}
              </button>
            ))}
        </>
      )}

      {/* Custom templates section */}
      {Array.isArray(customTemplates) && (
        <>
          <button
            className="dropdown-item section-header clickable"
            onClick={toggleCustomTemplates}
            onKeyDown={(e) => handleKeyDown(e, toggleCustomTemplates)}
            aria-expanded={!customTemplatesCollapsed}
          >
            <span>Custom Templates</span>{" "}
            <span className="section-toggle" aria-hidden="true">
              {customTemplatesCollapsed ? "▼" : "▲"}
            </span>
          </button>
          {!customTemplatesCollapsed &&
            customTemplates.length > 0 &&
            customTemplates.map((template) => (
              <button
                key={template.id}
                className={`dropdown-item ${selectedTemplateName === template.name ? "selected" : ""}`}
                onClick={() => onSelectTemplate(template.name)}
                onKeyDown={(e) =>
                  handleKeyDown(e, () => onSelectTemplate(template.name))
                }
                aria-pressed={selectedTemplateName === template.name}
              >
                {template.name}
              </button>
            ))}
        </>
      )}
    </div>
  );
};

export default TemplateDropdownContent;
